import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import CreateCloakModal from './CreateCloakModal';
import EditCloakModal from './EditCloakModal';
import { apiRequest, API_ENDPOINTS } from '../../../api/config';
import ReactDOM from 'react-dom';
import { createPortal } from 'react-dom';
import { useTheme } from '../ThemeContext';  // Import ThemeContext
import { getTranslation } from '../translations';  // 导入翻译函数

// Toast component - reusing the same style as in CreateCloakModal
const Toast = ({ message, duration = 2000, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose();
    }, duration);
    return () => clearTimeout(timer);
  }, [duration, onClose]);

  return (
    <div className="cloak-toast">
      <svg xmlns="http://www.w3.org/2000/svg" className="cloak-toast-icon" viewBox="0 0 20 20" fill="currentColor">
        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
      </svg>
      {message}
    </div>
  );
};

// Delete confirmation modal component
const DeleteConfirmModal = ({ isOpen, onClose, onConfirm, cloakName }) => {
  const { language } = useTheme();
  const t = getTranslation(language);

  if (!isOpen) return null;

  return (
    <div className="modal-overlay open" onClick={(e) => e.target === e.currentTarget && onClose()} style={{opacity: 1, pointerEvents: 'auto'}}>
      <div className="modal" style={{width: '400px'}}>
        <div className="modal-hd">
          <span className="modal-title">{t('confirmDelete')}</span>
          <span className="modal-close" onClick={onClose}>×</span>
        </div>
        <div className="modal-body" style={{padding: '20px', textAlign: 'center'}}>
          <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" fill="none" viewBox="0 0 24 24" stroke="var(--red)" style={{marginBottom: '12px'}}>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
          <p style={{color: 'var(--t2)', fontSize: '14px', lineHeight: 1.6}}>
            {t('confirmDeleteMessage').replace('{0}', cloakName)}
          </p>
        </div>
        <div className="modal-footer">
          <button type="button" className="btn btn-default" onClick={onClose}>
            {t('cancel')}
          </button>
          <button type="button" className="btn btn-danger" onClick={onConfirm}>
            {t('confirmDeletion')}
          </button>
        </div>
      </div>
    </div>
  );
};

// Membership status prompt modal
const MembershipRequiredModal = ({ isOpen, onClose, onGoToPlans }) => {
  const { language } = useTheme();
  const t = getTranslation(language);

  if (!isOpen) return null;

  return (
    <div className="modal-overlay open" onClick={(e) => e.target === e.currentTarget && onClose()} style={{opacity: 1, pointerEvents: 'auto'}}>
      <div className="modal" style={{width: '400px'}}>
        <div className="modal-hd">
          <span className="modal-title">{t('membershipRequired')}</span>
          <span className="modal-close" onClick={onClose}>×</span>
        </div>
        <div className="modal-body" style={{padding: '20px', textAlign: 'center'}}>
          <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" fill="none" viewBox="0 0 24 24" stroke="#f59e0b" style={{marginBottom: '12px'}}>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
          <p style={{color: 'var(--t2)', fontSize: '14px', lineHeight: 1.6}}>
            {t('membershipRequiredMessage')}
          </p>
        </div>
        <div className="modal-footer">
          <button type="button" className="btn btn-default" onClick={onClose}>
            {t('cancel')}
          </button>
          <button type="button" className="btn btn-primary" onClick={onGoToPlans}>
            {t('viewPlans')}
          </button>
        </div>
      </div>
    </div>
  );
};

// Status badge component
const StatusBadge = ({ status }) => {
  const { darkMode, language } = useTheme();
  const t = getTranslation(language);
  let badgeClass = "cloak-status-badge ";
  
  if (status === 'Active' || status === 'statusActive') {
    badgeClass += darkMode ? "cloak-status-active-dark" : "cloak-status-active";
  } else if (status === 'Paused' || status === 'statusPaused') {
    badgeClass += darkMode ? "cloak-status-paused-dark" : "cloak-status-paused";
  } else if (status === 'Failed' || status === 'statusFailed') {
    badgeClass += darkMode ? "cloak-status-failed-dark" : "cloak-status-failed";
  } else {
    badgeClass += darkMode ? "cloak-status-default-dark" : "cloak-status-default";
  }
  
  const getLocalizedStatus = () => {
    if (status === 'Active') return t('statusActive');
    if (status === 'Paused') return t('statusPaused');
    if (status === 'Failed') return t('statusFailed');
    return status;
  };
  
  return <span className={badgeClass}>{getLocalizedStatus()}</span>;
};

// Mode badge component
const ModeBadge = ({ mode }) => {
  const { darkMode, language } = useTheme();
  const t = getTranslation(language);
  let badgeClass = "cloak-mode-badge ";
  let label = "";
  
  switch (mode) {
    case 'cloak':
      badgeClass += darkMode ? "cloak-mode-cloak-dark" : "cloak-mode-cloak";
      label = t('cloakMode');
      break;
    case 'fullOpen':
      badgeClass += darkMode ? "cloak-mode-fullopen-dark" : "cloak-mode-fullopen";
      label = t('fullOpenMode');
      break;
    case 'audit':
      badgeClass += darkMode ? "cloak-mode-audit-dark" : "cloak-mode-audit";
      label = t('auditMode');
      break;
    default:
      badgeClass += darkMode ? "cloak-mode-default-dark" : "cloak-mode-default";
      label = t('unknown');
  }
  
  return <span className={badgeClass}>{label}</span>;
};

// Region badge component
const RegionBadge = ({ regions = [] }) => {
  const { darkMode, language } = useTheme();
  
  if (!regions || regions.length === 0) return <span className="cloak-region-empty">-</span>;
  
  // Display first 2, others display "+n"
  const display = regions.slice(0, 2);
  const remaining = regions.length > 2 ? regions.length - 2 : 0;
  
  return (
    <div className="cloak-region-container">
      {display.map((region, index) => {
        // Ensure we're rendering a string, not an object
        const displayText = language === 'en' 
          ? (region.code || (typeof region === 'string' ? region : JSON.stringify(region)))
          : (region.name || (typeof region === 'string' ? region : JSON.stringify(region)));
        
        return (
          <span key={index} className="cloak-region-badge">
            {displayText}
          </span>
        );
      })}
      {remaining > 0 && (
        <span className="cloak-region-badge">
          +{remaining}
        </span>
      )}
    </div>
  );
};

// CopyableLink component - link + copy button style like CloakLogs
const CopyableLink = ({ url, label }) => {
  const { darkMode, language } = useTheme();
  const t = getTranslation(language);
  const [showToast, setShowToast] = useState(false);
  
  const fallbackCopyToClipboard = (text) => {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    
    let success = false;
    try {
      success = document.execCommand('copy');
    } catch (err) {
      success = false;
    }
    
    document.body.removeChild(textArea);
    return success;
  };
  
  const copyToClipboard = async (text) => {
    if (navigator.clipboard && window.isSecureContext) {
      try {
        await navigator.clipboard.writeText(text);
        return true;
      } catch (err) {
        return fallbackCopyToClipboard(text);
      }
    } else {
      return fallbackCopyToClipboard(text);
    }
  };
  
  const handleCopy = async (e) => {
    e.stopPropagation();
    const success = await copyToClipboard(url);
    if (success) {
      setShowToast(true);
      setTimeout(() => setShowToast(false), 1500);
    }
  };
  
  if (!url) return <span style={{color: 'var(--t3)'}}>-</span>;
  
  const displayLabel = label || url;
  
  return (
    <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
      <a 
        href={url} 
        target="_blank" 
        rel="noopener noreferrer"
        className="link-primary"
        style={{
          color: darkMode ? 'var(--accent)' : 'var(--accent-d)',
          wordBreak: 'break-all'
        }}
      >
        {displayLabel}
      </a>
      <button
        onClick={handleCopy}
        style={{
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: darkMode ? 'var(--bg-input)' : 'var(--bg-card)',
          border: '1px solid var(--border)',
          borderRadius: '6px',
          color: 'var(--t2)',
          cursor: 'pointer',
          padding: '6px',
          fontSize: '14px',
          transition: 'all 0.2s'
        }}
        title={t('copyLink')}
      >
        📋
        {showToast && (
          <span style={{
            position: 'absolute',
            marginTop: '-32px',
            background: 'var(--success)',
            color: 'white',
            padding: '4px 8px',
            borderRadius: '4px',
            fontSize: '12px',
            whiteSpace: 'nowrap'
          }}>
            ✓
          </span>
        )}
      </button>
    </div>
  );
};

const CloakList = () => {
  const { darkMode, language } = useTheme();
  // 获取翻译函数
  const t = getTranslation(language);
  
  const [loading, setLoading] = useState(true);
  const [cloaks, setCloaks] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [modeFilter, setModeFilter] = useState('All');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    pageSize: 10,
    totalItems: 0
  });
  
  // 编辑斗篷相关状态
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingCloak, setEditingCloak] = useState(null);
  
  // 会员状态相关状态
  const [isMembershipModalOpen, setIsMembershipModalOpen] = useState(false);
  const [userStatus, setUserStatus] = useState({
    isMember: false,
    membershipInfo: null,
    remainingCalls: 0
  });
  
  // 删除确认弹窗状态
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [cloakToDelete, setCloakToDelete] = useState(null);
  
  // 添加成功消息提示状态
  const [successMessage, setSuccessMessage] = useState(null);
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  
  // 使用useRef跟踪组件是否已经加载过数据，避免重复请求
  const isInitialDataFetched = useRef(false);
  // 使用useRef跟踪搜索条件变化是否是由于初始化引起的
  const isInitialSearchFilterSet = useRef(true);
  
  // 添加路由导航
  const navigate = useNavigate();
  
  // 获取当前用户会员状态
  const fetchUserStatus = async () => {
    try {
      const response = await apiRequest(API_ENDPOINTS.USER.PROFILE, {}, true);
      
      if (response && response.status === 'success' && response.data) {
        const userData = response.data;
        
        // 检查用户是否是有效会员
        const isMember = userData.isMember || false;
        let membershipValid = false;
        
        if (userData.membershipExpiresAt) {
          // 检查会员是否过期
          membershipValid = new Date(userData.membershipExpiresAt) > new Date();
        }
        
        setUserStatus({
          isMember: isMember,
          membershipInfo: userData.membershipInfo || null,
          isValidMember: isMember && membershipValid,
          remainingCalls: userData.remainingCalls || 0
        });
      }
    } catch (error) {
      console.error('Failed to get user membership status:', error);
      // 如果获取失败，假设用户不是会员
      setUserStatus({
        isMember: false,
        membershipInfo: null,
        isValidMember: false,
        remainingCalls: 0
      });
    }
  };
  
  // 显示成功消息的函数
  const showSuccess = (message) => {
    setSuccessMessage(message);
    setShowSuccessToast(true);
    
    // 3秒后自动隐藏消息
    setTimeout(() => {
      setShowSuccessToast(false);
      setTimeout(() => setSuccessMessage(null), 300); // 动画结束后清空消息
    }, 3000);
  };
  
  // 处理打开创建斗篷弹窗，检查会员状态或可用额度
  const handleOpenCreateModal = () => {
    // 检查用户是否是有效会员或有可用额度
    const canCreateCloak = userStatus.isValidMember || (userStatus.remainingCalls > 0);
    
    if (!canCreateCloak) {
      // 既不是会员也没有可用额度，打开会员提醒模态框
      setIsMembershipModalOpen(true);
    } else {
      // 是有效会员或有可用额度，打开创建斗篷弹窗
      setIsCreateModalOpen(true);
    }
  };
  
  // 打开创建斗篷弹窗
  const openCreateModal = () => {
    setIsCreateModalOpen(true);
  };
  
  // 关闭创建斗篷弹窗
  const closeCreateModal = () => {
    setIsCreateModalOpen(false);
  };
  
  // 关闭会员提醒模态框
  const closeMembershipModal = () => {
    setIsMembershipModalOpen(false);
  };
  
  // 导航到套餐页面
  const goToPlansPage = () => {
    closeMembershipModal();
    navigate('/dashboard/cloak/plans');
  };
  
  // 处理创建斗篷表单提交
  const handleCreateCloak = async (cloakData) => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('Submitting data:', cloakData);
      
      const response = await apiRequest(API_ENDPOINTS.CLOAK.CREATE, {
        name: cloakData.name,
        realUrl: cloakData.realUrl,
        auditUrl: cloakData.auditUrl,
        shortLink: cloakData.shortLink,
        mode: cloakData.mode,
        selectedRegions: Array.isArray(cloakData.selectedRegions) 
          ? cloakData.selectedRegions.map(region => typeof region === 'string' ? region : region.code)
          : [],
        platforms: Array.isArray(cloakData.platforms) ? cloakData.platforms : []
      }, true, 'POST');
      
      console.log('API create cloak response:', response);
      console.log('Short link field check:', {
        'response.shortLink': response.shortLink,
        'response.shortLink': response.shortLink,
        'response.domain': response.domain,
        'response.url': response.url,
        'data field': response.data,
        'Complete response': response
      });
      
      // 重新加载斗篷列表
      await fetchCloaks();
      
      // 显示成功消息
      showSuccess(t('cloakCreated').replace('{0}', cloakData.name));
      
      // 返回响应数据给调用者，这样 CreateCloakModal 可以获取到 shortLink
      return response;
    } catch (error) {
      console.error('Error creating cloak:', error);
      setError(error.message || t('createCloakFailed'));
      return null; // 发生错误时也要返回一个值
    } finally {
      setLoading(false);
    }
  };
  
  // 处理删除斗篷
  const handleDeleteCloak = (id) => {
    // 找到要删除的斗篷对象
    const cloak = cloaks.find(cloak => cloak.id === id);
    if (cloak) {
      setCloakToDelete(cloak);
      setIsDeleteModalOpen(true);
    }
  };
  
  // 执行删除操作
  const confirmDeleteCloak = async () => {
    if (!cloakToDelete) return;
    
    try {
      setLoading(true);
      setError(null);

      // 完全按照update的方式调用delete函数
      await apiRequest(API_ENDPOINTS.CLOAK.DELETE(cloakToDelete.id), {}, true, 'DELETE');

      // Reload cloak list
      await fetchCloaks();
      
      // Show success message
      showSuccess(t('cloakDeleted').replace('{0}', cloakToDelete.name));
      
      // Close delete confirmation dialog
      setIsDeleteModalOpen(false);
      setCloakToDelete(null);
    } catch (error) {
      console.error('Error deleting cloak:', error);
      setError(error.message || t('deleteCloakFailed'));
    } finally {
      setLoading(false);
    }
  };
  
  // 关闭删除确认弹窗
  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setCloakToDelete(null);
  };
  
  // 处理编辑斗篷
  const handleEditCloak = (id) => {
    // 找到要编辑的斗篷
    const cloak = cloaks.find(cloak => cloak.id === id);
    if (cloak) {
      // 将数据格式化为编辑表单所需的格式
      const formattedCloak = {
        id: cloak.id,
        name: cloak.name,
        realUrl: cloak.targetUrl || cloak.realUrl,
        auditUrl: cloak.auditUrl,
        shortLink: cloak.shortLink,
        mode: cloak.mode,
        platforms: Array.isArray(cloak.platforms) ? cloak.platforms : [],
        selectedRegions: Array.isArray(cloak.selectedRegions) 
          ? cloak.selectedRegions.map(region => {
              // 确保每个region都是一个包含name和code的对象
              if (typeof region === 'string') {
                // 如果是字符串ID，尝试在regions列表中找到对应的完整对象
                // 注意: 这需要regions列表是可用的，如果不可用，可能需要进一步修改
                return { code: region, name: region }; // 临时使用code作为名称
              } else if (region && typeof region === 'object') {
                // 如果已经是对象，确保有name和code属性
                return {
                  name: region.name || region.code || '',
                  code: region.code || ''
                };
              }
              return null;
            }).filter(Boolean) // 过滤掉null值
          : []
      };
      
      setEditingCloak(formattedCloak);
      setIsEditModalOpen(true);
    }
  };
  
  // Handle edit form submission
  const handleEditSubmit = async (formData) => {
    try {
      setLoading(true);
      setError(null);
      
      // Process selectedRegions format
      let processedRegions = [];
      
      if (Array.isArray(formData.selectedRegions)) {
        processedRegions = formData.selectedRegions.map(region => {
          if (!region) return null; // Filter undefined values
          if (typeof region === 'string') return region;
          if (typeof region === 'object' && region.code) return region.code;
          return null;
        }).filter(Boolean); // Filter null values
      }
      
      console.log("Processed selectedRegions:", processedRegions);
      
      // Send update request - fix URL issue
      const response = await apiRequest(API_ENDPOINTS.CLOAK.UPDATE(editingCloak.id), {
        name: formData.name,
        realUrl: formData.realUrl,
        auditUrl: formData.auditUrl,
        shortLink: formData.shortLink,
        mode: formData.mode,
        selectedRegions: processedRegions,
        platforms: Array.isArray(formData.platforms) ? formData.platforms : []
      }, true, 'PUT');
      
      // Reload cloak list
      await fetchCloaks();
      
      // Close edit modal
      closeEditModal();
      
      // Show success message
      showSuccess(t('cloakUpdated').replace('{0}', formData.name));
      
      // Return response data to caller
      return response;
    } catch (error) {
      console.error('Error updating cloak:', error);
      setError(error.message || t('updateCloakFailed'));
      return null; // Return null on error
    } finally {
      setLoading(false);
    }
  };
  
  // 关闭编辑弹窗
  const closeEditModal = () => {
    setIsEditModalOpen(false);
    setEditingCloak(null);
  };
  
  // 获取斗篷列表数据
  const fetchCloaks = async (page = 1, overrideFilters = null) => {
    try {
      setLoading(true);
      setError(null);
      
      const search = overrideFilters ? overrideFilters.search : searchTerm;
      const mode = overrideFilters ? overrideFilters.mode : modeFilter;
      
      // 构建查询参数
      const params = new URLSearchParams({
        page: page,
        perPage: pagination.pageSize
      });
      
      // 添加筛选条件
      if (search) {
        params.append('search', search);
      }
      
      if (mode !== 'All') {
        const modeMap = {
          'Cloak': 'cloak',
          'Full Open': 'fullOpen',
          'Audit': 'audit'
        };
        params.append('mode', modeMap[mode] || mode);
      }
      
      // 使用 apiRequest 替代直接的 fetch 调用
      const response = await apiRequest(`${API_ENDPOINTS.CLOAK.LIST}?${params.toString()}`, {}, true, 'GET');
      
      // Java后端返回 {status, data: {items, total, page, perPage}} 格式
      // Python后端返回 {items, total, page, perPage} 格式
      const data = response?.data || response;
      
      // 验证响应数据格式
      if (!data.items || !Array.isArray(data.items)) {
        throw new Error('Server returned incorrect data format');
      }
      
      // 处理数据字段映射，将后端字段名与前端字段名对应
      const baseUrl = window.location.origin;
      const processedData = data.items.map(cloak => {
        // 确保短链是完整URL形式
        let fullShortLink = cloak.shortLink;
        if (fullShortLink && fullShortLink.startsWith('/t/')) {
          fullShortLink = `${baseUrl}${fullShortLink}`;
        }
        
        return {
          id: cloak.id,
          name: cloak.name,
          domain: fullShortLink.replace(/^https?:\/\//, ''),
          shortLink: fullShortLink,
          targetUrl: cloak.realUrl,
          auditUrl: cloak.auditUrl,
          created: new Date(cloak.createdAt),
          visits: cloak.visits,
          realVisits: cloak.realVisits,
          auditVisits: cloak.auditVisits,
          mode: cloak.mode,
          platforms: Array.isArray(cloak.platforms) ? cloak.platforms : [],
          selectedRegions: Array.isArray(cloak.selectedRegions)
            ? cloak.selectedRegions.map(region => {
                // 确保每个region都是一个包含name和code的对象
                if (typeof region === 'string') {
                  // 如果是字符串ID，尝试在regions列表中找到对应的完整对象
                  return { code: region, name: region };
                } else if (region && typeof region === 'object') {
                  // 后端返回的region结构：{ code: string, name: string }
                  return {
                    name: region.name || region.code || '',
                    code: region.code || ''
                  };
                }
                return null;
              }).filter(Boolean)
            : []
        };
      });
      
      setCloaks(processedData);
      
      // 更新分页信息
      setPagination({
        currentPage: data.page || 1,
        totalPages: Math.ceil((data.total || 0) / (data.perPage || 10)),
        pageSize: data.perPage || 10,
        totalItems: data.total || 0
      });
    } catch (error) {
      console.error('Failed to load cloak list:', error);
      setError(error.message || 'Failed to load cloak list, please try again later');
      setCloaks([]);
    } finally {
      setLoading(false);
    }
  };
  
  // 处理页面切换
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      fetchCloaks(newPage);
    }
  };
  

  // 初始加载数据
  useEffect(() => {
    // 确保数据只加载一次
    if (!isInitialDataFetched.current) {
      fetchCloaks(1);
      fetchUserStatus(); // 加载用户会员状态
      isInitialDataFetched.current = true;
    }
  }, []);
  
  // 格式化日期
  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };
  
  // 获取模式显示名称
  const getModeDisplayName = (mode) => {
    const modeMap = {
      'cloak': 'Cloak',
      'fullOpen': 'Full Open',
      'audit': 'Audit'
    };
    return modeMap[mode] || mode;
  };
  
  // 删除前端过滤逻辑，直接使用后端过滤后的结果
  const filteredCloaks = cloaks;
  
  if (loading) {
    return (
      <div className="dashboard-loading">
        <div className="spinner"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-state-error-card">
        <div className="page-state-icon-error">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h2 className="page-state-title">{t('error')}</h2>
        <p className="page-state-message">{error}</p>
        <button 
          className="dashboard-btn primary-btn"
          onClick={() => fetchCloaks(1)}
        >
          {t('retry')}
        </button>
      </div>
    );
  }
  
  return (
    <div className="page active">
      <div className="page-hd">
        <div>
          <div className="page-title">{t('cloakList')}</div>
        </div>
        <div className="gap-row">
          <button className="btn btn-primary" onClick={handleOpenCreateModal}>
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="5" x2="12" y2="19"></line>
              <line x1="5" y1="12" x2="19" y2="12"></line>
            </svg>
            {t('createNewCloak')}
          </button>
        </div>
      </div>
      
      <div className="filter-bar">
        <div className="filter-fields">
          <div className="filter-item">
            <span className="filter-label">{t('cloakName')}</span>
            <input className="fi" placeholder={t('searchPlaceholder')} value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
          </div>
          <div className="filter-item">
            <span className="filter-label">{t('mode')}</span>
            <select className="fi fi-short" value={modeFilter} onChange={(e) => setModeFilter(e.target.value)}>
              <option value="All">{t('allModes')}</option>
              <option value="Cloak">{t('cloakMode')}</option>
              <option value="Full Open">{t('fullOpenMode')}</option>
              <option value="Audit">{t('auditMode')}</option>
            </select>
          </div>
        </div>
        <div className="filter-btns">
          <button className="btn btn-primary" onClick={() => fetchCloaks(1)}>🔍 {t('search')}</button>
          <button className="btn btn-default" onClick={() => {
            setSearchTerm('');
            setModeFilter('All');
            fetchCloaks(1, { search: '', mode: 'All' });
          }}>↺ {t('reset')}</button>
        </div>
      </div>

      <div className="table-wrap">
        {filteredCloaks.length > 0 ? (
          <div>
            <table>
              <thead>
                <tr>
                  <th>{t('cloakName')}</th>
                  <th>{t('shortLink')}</th>
                  <th>{t('realLink')}</th>
                  <th>{t('auditLink')}</th>
                  <th>{t('mode')}</th>
                  <th>{t('targetRegions')}</th>
                  <th>{t('totalVisits')}</th>
                  <th>{t('realLinkVisits')}</th>
                  <th>{t('auditLinkVisits')}</th>
                  <th>{t('createdDate')}</th>
                  <th>{t('actions')}</th>
                </tr>
              </thead>
              <tbody>
                {filteredCloaks.map(cloak => (
                  <tr key={cloak.id}>
                    <td>{cloak.name}</td>
                    <td><CopyableLink url={cloak.shortLink || `https://${cloak.domain}`} label={cloak.domain} /></td>
                    <td><CopyableLink url={cloak.targetUrl} /></td>
                    <td><CopyableLink url={cloak.auditUrl} /></td>
                    <td><ModeBadge mode={cloak.mode} /></td>
                    <td><RegionBadge regions={cloak.selectedRegions} /></td>
                    <td>{(cloak.visits || 0).toLocaleString()}</td>
                    <td>{(cloak.realVisits || 0).toLocaleString()}</td>
                    <td>{(cloak.auditVisits || 0).toLocaleString()}</td>
                    <td>{formatDate(cloak.created)}</td>
                    <td>
                      <div className="gap-row">
                        <button className="btn btn-default btn-sm" onClick={() => handleEditCloak(cloak.id)}>{t('edit')}</button>
                        <button className="btn btn-danger btn-sm" onClick={() => handleDeleteCloak(cloak.id)}>{t('delete')}</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="pagination">
              <span>{t('totalRecords').replace('{0}', pagination.totalItems)}</span>
              <div className="pagi-btns">
                {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                  let pageNum;
                  if (pagination.totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (pagination.currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (pagination.currentPage >= pagination.totalPages - 2) {
                    pageNum = pagination.totalPages - 4 + i;
                  } else {
                    pageNum = pagination.currentPage - 2 + i;
                  }
                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      className={`pagi-btn ${pagination.currentPage === pageNum ? 'active' : ''}`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        ) : (
          <div className="empty">
            {cloaks.length > 0 ? t('noMatchingCloaks') : t('noCloakData')}
          </div>
        )}
      </div>

      {/* 创建斗篷弹窗 - 使用 Portal 渲染到 body */}
      {isCreateModalOpen && ReactDOM.createPortal(
        <CreateCloakModal
          isOpen={isCreateModalOpen}
          onClose={closeCreateModal}
          onSubmit={handleCreateCloak}
        />,
        document.body
      )}

      {/* 编辑斗篷弹窗 - 使用 Portal 渲染到 body */}
      {isEditModalOpen && ReactDOM.createPortal(
        <EditCloakModal
          isOpen={isEditModalOpen}
          onClose={closeEditModal}
          onSubmit={handleEditSubmit}
          initialData={editingCloak}
        />,
        document.body
      )}

      {/* 删除确认弹窗 */}
      <DeleteConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={closeDeleteModal}
        onConfirm={confirmDeleteCloak}
        cloakName={cloakToDelete?.name}
      />

      {/* 会员状态提醒弹窗 */}
      <MembershipRequiredModal
        isOpen={isMembershipModalOpen}
        onClose={closeMembershipModal}
        onGoToPlans={goToPlansPage}
      />
    </div>
  );
};

export default CloakList;